// True 2-stem separation (vocals + instrumental) in the browser.
//
// Uses Deezer's Spleeter 2-stems model (exported to ONNX by k2-fsa/sherpa-onnx)
// via onnxruntime-web. Spleeter is a magnitude-spectrogram U-Net: it takes a
// magnitude STFT and predicts per-source magnitudes; we do the STFT/iSTFT in JS
// (HT-Demucs' baked iSTFT uses ops onnxruntime-web can't run). Two small models
// (~39 MB each, conv-only) load on WebGPU (wasm fallback) and run fast.
//
// Pipeline (matches sherpa-onnx's reference `separate_onnx.py` exactly):
//   STFT  n_fft=4096, hop=1024, periodic Hann, center=false (unnormalized DFT)
//   keep first 1024 freq bins, segment into [2, splits, 512, 1024] magnitudes
//   run vocals + accompaniment models → ratio mask
//     v_mask = (v² + 5e-11) / (v² + a² + 1e-10)   (on the 1024 kept bins)
//   apply mask to the mixture's complex STFT, iSTFT → vocals waveform
//   instrumental = mix − vocals (time domain; keeps content above ~11 kHz that
//   the band-limited model can't represent, and equals accompaniment below it).
import { AudioEngine } from "./engine";

const SR = 44100;
const N_FFT = 4096, HOP = 1024, BINS = N_FFT / 2 + 1; // 2049
const F_KEEP = 1024, T = 512; // model freq bins / frames-per-segment
const ORT_VER = "1.20.1";
// Served same-origin (no CORS): public/models/{vocals,accompaniment}.onnx →
// /models/... in dev and prod. Overridable via localStorage.
const MODELS_BASE = localStorage.getItem("webdaw.stemModelBase") || "/models/";

export type Progress = (phase: "download" | "separate", frac: number) => void;

// --- periodic Hann window (matches kaldi-native-fbank / TF periodic Hann) ---
const WIN = (() => { const w = new Float32Array(N_FFT); for (let i = 0; i < N_FFT; i++) w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / N_FFT); return w; })();

// In-place iterative radix-2 FFT (n must be a power of two). inverse scales by 1/n.
function fft(re: Float64Array, im: Float64Array, inverse: boolean): void {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) { const tr = re[i]; re[i] = re[j]; re[j] = tr; const ti = im[i]; im[i] = im[j]; im[j] = ti; }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const half = len >> 1;
    const ang = (2 * Math.PI / len) * (inverse ? 1 : -1);
    const wr = Math.cos(ang), wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1, ci = 0;
      for (let k = 0; k < half; k++) {
        const ar = re[i + k], ai = im[i + k];
        const br = re[i + k + half], bi = im[i + k + half];
        const vr = br * cr - bi * ci, vi = br * ci + bi * cr;
        re[i + k] = ar + vr; im[i + k] = ai + vi;
        re[i + k + half] = ar - vr; im[i + k + half] = ai - vi;
        const ncr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = ncr;
      }
    }
  }
  if (inverse) for (let i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
}

interface Spec { real: Float32Array; imag: Float32Array; numFrames: number; } // first F_KEEP bins

/** Forward STFT of one channel; stores only the first F_KEEP bins' complex
 *  values (the only ones the model masks; higher bins get zeroed anyway). */
function stftChannel(sig: Float32Array): Spec {
  const n = Math.max(N_FFT, sig.length);
  const numFrames = 1 + Math.floor((n - N_FFT) / HOP);
  const real = new Float32Array(numFrames * F_KEEP), imag = new Float32Array(numFrames * F_KEEP);
  const re = new Float64Array(N_FFT), im = new Float64Array(N_FFT);
  for (let f = 0; f < numFrames; f++) {
    const s = f * HOP;
    for (let k = 0; k < N_FFT; k++) { const j = s + k; re[k] = (j < sig.length ? sig[j] : 0) * WIN[k]; im[k] = 0; }
    fft(re, im, false);
    const o = f * F_KEEP;
    for (let k = 0; k < F_KEEP; k++) { real[o + k] = re[k]; imag[o + k] = im[k]; }
  }
  return { real, imag, numFrames };
}

/** Inverse STFT from masked complex (first F_KEEP bins; bins ≥ F_KEEP are 0).
 *  Weighted overlap-add with the synthesis window, normalized by Σ w². */
function istftChannel(real: Float32Array, imag: Float32Array, numFrames: number): Float32Array {
  const numSamples = N_FFT + (numFrames - 1) * HOP;
  const out = new Float64Array(numSamples), denom = new Float64Array(numSamples);
  const re = new Float64Array(N_FFT), im = new Float64Array(N_FFT);
  for (let f = 0; f < numFrames; f++) {
    re.fill(0); im.fill(0);
    const o = f * F_KEEP;
    for (let k = 0; k < F_KEEP; k++) { re[k] = real[o + k]; im[k] = imag[o + k]; }
    // Hermitian-symmetric upper half for a real-valued inverse transform.
    for (let k = 1; k < N_FFT - BINS + 1; k++) { re[N_FFT - k] = re[k]; im[N_FFT - k] = -im[k]; }
    fft(re, im, true);
    const s = f * HOP;
    for (let k = 0; k < N_FFT; k++) { const wv = WIN[k]; out[s + k] += re[k] * wv; denom[s + k] += wv * wv; }
  }
  const result = new Float32Array(numSamples);
  for (let i = 0; i < numSamples; i++) result[i] = denom[i] ? out[i] / denom[i] : 0;
  return result;
}

let ortPromise: Promise<any> | null = null;
async function getOrt(): Promise<any> {
  if (!ortPromise) {
    ortPromise = (async () => {
      const base = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ORT_VER}/dist/`;
      const ort: any = await import(/* @vite-ignore */ base + "ort.webgpu.bundle.min.mjs");
      ort.env.wasm.wasmPaths = base;
      ort.env.wasm.numThreads = 1; // no cross-origin isolation here → no SharedArrayBuffer
      ort.env.logLevel = "error";
      return ort;
    })();
  }
  return ortPromise;
}

interface Loaded { ort: any; vocals: any; accomp: any; provider: string; }
let sessionPromise: Promise<Loaded> | null = null;

async function fetchModel(url: string, onChunk: (n: number) => void): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Model download failed (${res.status}) for ${url}`);
  if (!res.body) return res.arrayBuffer();
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = []; let loaded = 0;
  for (;;) { const { done, value } = await reader.read(); if (done) break; chunks.push(value); loaded += value.length; onChunk(value.length); }
  const out = new Uint8Array(loaded); let o = 0; for (const c of chunks) { out.set(c, o); o += c.length; }
  return out.buffer;
}

async function getSessions(onProgress?: Progress): Promise<Loaded> {
  if (sessionPromise) return sessionPromise;
  sessionPromise = (async () => {
    const ort = await getOrt();
    // Two ~39 MB downloads; report combined progress against a known total.
    const TOTAL = 39_318_336 + 39_318_343;
    let loaded = 0;
    const tick = (n: number) => { loaded += n; onProgress?.("download", Math.min(1, loaded / TOTAL)); };
    const [vBuf, aBuf] = await Promise.all([
      fetchModel(MODELS_BASE + "vocals.onnx", tick),
      fetchModel(MODELS_BASE + "accompaniment.onnx", tick),
    ]);
    const make = (buf: ArrayBuffer, ep: string) => ort.InferenceSession.create(buf, { executionProviders: [ep], graphOptimizationLevel: "all" });
    let provider = "webgpu";
    let vocals: any, accomp: any;
    try {
      vocals = await make(vBuf, "webgpu"); accomp = await make(aBuf, "webgpu");
    } catch (e) {
      console.warn("WebGPU EP unavailable, falling back to wasm:", e);
      provider = "wasm";
      vocals = await make(vBuf, "wasm"); accomp = await make(aBuf, "wasm");
    }
    return { ort, vocals, accomp, provider };
  })();
  return sessionPromise;
}

/** Resample an AudioBuffer to 44.1 kHz stereo. */
async function toModelRate(buffer: AudioBuffer): Promise<{ L: Float32Array; R: Float32Array; length: number }> {
  const len = Math.max(1, Math.ceil(buffer.duration * SR));
  const off = new OfflineAudioContext(2, len, SR);
  const src = off.createBufferSource();
  src.buffer = buffer; src.connect(off.destination); src.start();
  const rs = await off.startRendering();
  const L = rs.getChannelData(0);
  const R = rs.numberOfChannels > 1 ? rs.getChannelData(1) : rs.getChannelData(0);
  return { L: new Float32Array(L), R: new Float32Array(R), length: rs.length };
}

export function webgpuAvailable(): boolean {
  return typeof (navigator as any).gpu !== "undefined";
}

/** Separate a clip's audio into vocals + instrumental AudioBuffers (44.1 kHz). */
export async function separateStems(
  engine: AudioEngine,
  buffer: AudioBuffer,
  onProgress?: Progress,
): Promise<{ vocals: AudioBuffer; instrumental: AudioBuffer; provider: string }> {
  const { ort, vocals: vSession, accomp: aSession, provider } = await getSessions(onProgress);
  const mix = await toModelRate(buffer);

  // Pad by one full window on each side so every real sample is covered by a
  // full set of overlapping windows (Σw² ≈ const); without this the weighted
  // overlap-add denominator falls to ~0 at the edges and blows the output up.
  const PAD = N_FFT;
  const pad = (a: Float32Array) => { const p = new Float32Array(a.length + 2 * PAD); p.set(a, PAD); return p; };

  // forward STFT of both channels (stores first F_KEEP bins' complex values)
  const specL = stftChannel(pad(mix.L)), specR = stftChannel(pad(mix.R));
  const numFrames = specL.numFrames;
  const channels: Spec[] = [specL, specR];
  const splits = Math.max(1, Math.ceil(numFrames / T));

  const segFloats = 2 * T * F_KEEP; // one [2,1,512,1024] segment
  // reusable model input buffer
  const inData = new Float32Array(segFloats);

  for (let s = 0; s < splits; s++) {
    // build magnitude input for this segment, both channels
    inData.fill(0);
    for (let ch = 0; ch < 2; ch++) {
      const sp = channels[ch];
      for (let t = 0; t < T; t++) {
        const gf = s * T + t;
        if (gf >= numFrames) break;
        const so = gf * F_KEEP;
        const io = (ch * T + t) * F_KEEP;
        for (let f = 0; f < F_KEEP; f++) { const re = sp.real[so + f], im = sp.imag[so + f]; inData[io + f] = Math.sqrt(re * re + im * im); }
      }
    }
    const x = new ort.Tensor("float32", inData.slice(0), [2, 1, T, F_KEEP]);
    const vr = await vSession.run({ x });
    const ar = await aSession.run({ x });
    const vOut = (vr.y ?? vr[vSession.outputNames[0]]).data as Float32Array;
    const aOut = (ar.y ?? ar[aSession.outputNames[0]]).data as Float32Array;
    // ratio mask, applied in place to each channel's stored complex spectrum
    for (let ch = 0; ch < 2; ch++) {
      const sp = channels[ch];
      for (let t = 0; t < T; t++) {
        const gf = s * T + t;
        if (gf >= numFrames) break;
        const so = gf * F_KEEP;
        const oo = (ch * T + t) * F_KEEP;
        for (let f = 0; f < F_KEEP; f++) {
          const v = vOut[oo + f], a = aOut[oo + f];
          const mask = (v * v + 5e-11) / (v * v + a * a + 1e-10);
          sp.real[so + f] *= mask; sp.imag[so + f] *= mask;
        }
      }
    }
    onProgress?.("separate", (s + 1) / splits);
  }

  // iSTFT the masked spectra → vocals; instrumental = mix − vocals
  const vL = istftChannel(specL.real, specL.imag, numFrames);
  const vR = istftChannel(specR.real, specR.imag, numFrames);

  const n = mix.length;
  const vocals = engine.ctx.createBuffer(2, n, SR);
  const instrumental = engine.ctx.createBuffer(2, n, SR);
  const voL = vocals.getChannelData(0), voR = vocals.getChannelData(1);
  const inL = instrumental.getChannelData(0), inR = instrumental.getChannelData(1);
  for (let i = 0; i < n; i++) {
    // shift back past the leading pad to realign with the original signal
    const a = i + PAD < vL.length ? vL[i + PAD] : 0, b = i + PAD < vR.length ? vR[i + PAD] : 0;
    voL[i] = a; voR[i] = b;
    inL[i] = mix.L[i] - a; inR[i] = mix.R[i] - b;
  }
  return { vocals, instrumental, provider };
}
